import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SectionCard } from '../SectionCard';

const StyledTitle = styled(Typography)`
    font-weight: 600;
    font-size: 18px;
    color: ${({ theme }: any) => theme.palette.neutralWhite.main};
    ${({ theme }: any) => theme.typography.fontFamilyBrand};
`;

const StyledSubtitle = styled(Typography)`
    font-weight: 400;
    font-size: 14px;
    color: ${({ theme }: any) => theme.palette.neutralWhite.main};
    margin-top: 4px;
    margin-bottom: 16px;
`;

const StyledLinearProgress = styled(Box) <{ value: number }>`
    height: 16px;
    border-radius: 12px;
    text-align: center;
    width: 100%;
    background-color: ${({ theme }: any) => theme.palette.disableFormBlack.main};
    padding: 1px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    & .counter {
        font-size: 12px;
        font-weight: 500;
        text-align: center;
        color: ${({ theme, value }: any) =>
        value > 45 ? theme.palette.textInvert.main : theme.palette.primaryPurple30.main};
        z-index: 10;
    }
`;

const ProgressBar = styled(Box) <{ value: number }>`
    width: ${({ value }) => `${value}%`};
    position: absolute;
    left: 0;
    background: ${({ theme }: any) => theme.palette.otherSuccess.main};
    height: 16px;
    border-radius: 8px;
    z-index: 9;
`;

const StyledList = styled(List)({
    marginTop: 16,
    padding: 0,
});

const StyledListItem = styled(ListItem)<{ completed?: boolean }>(({ theme, completed }) => ({
    padding: '0 0 0 0',
    color: theme.palette.otherSuccess.main,
    marginBottom: '12px',
    '&:last-child': {
        marginBottom: 0,
    },
    '& .MuiListItemIcon-root': {
        // minWidth: 32,
        width: 20,
        height: 20,
        maxWidth: 20,
        minWidth: 20,
        color: completed ? theme.palette.otherSuccess.main : theme.palette.warningOrange.main,
    },
    '& .MuiListItemText-primary': {
        fontWeight: 600,
        fontSize: 14,
        lineHeight: '100%',
        color: completed ? theme.palette.otherSuccess.main : theme.palette.warningOrange.main,
    },
}));

const sectionMap = [
    { key: 'profile_info', label: 'Informations du profil' },
    { key: 'profile_photo', label: 'Photo de profil' },
    { key: 'banner_image', label: 'Image de bannière' },
    { key: 'biography', label: 'Biographie' },
    { key: 'salary', label: 'Salaire' },
    { key: 'contact', label: 'Contact' },
    { key: 'professional_skills', label: 'Mes compétences professionnelles' },
    { key: 'interpersonal_skill', label: 'Compétence interpersonnelle' },
    { key: 'experience', label: 'Expérience' },
    { key: 'education', label: 'Éducation' },
    { key: 'certificates_training', label: 'Certificats et formations' },
];

interface CardCompleteProfileProps {
    value: number;
    completedSections?: string[];
}

const TalentProfileCompletionRate = ({ value, completedSections = [] }: CardCompleteProfileProps) => {
    const completionValue = Math.min(100, Math.max(0, Math.round(value || 0)));
    const completedSectionSet = new Set(completedSections);
    if (completionValue === 100) {
        return null;
    }
    return (
        <SectionCard sx={{flex: 1, padding: 2, }}>
            {completionValue !== 100 && (
                <>
                    <StyledTitle>Complétez votre profil</StyledTitle>
                    <StyledSubtitle>
                        Complétez votre profil et multipliez votre visibilité auprès des employeurs
                    </StyledSubtitle>
                    <StyledLinearProgress value={completionValue}>
                        <ProgressBar value={completionValue} />
                        <Typography className="counter">{completionValue}%</Typography>
                    </StyledLinearProgress>
                    <StyledList>
                        {sectionMap.filter(section => !completedSectionSet.has(section.key)).map((section) => {
                            return (
                            <StyledListItem key={section.key} disableGutters>
                                <ListItemText primary={section.label} />
                                <ListItemIcon>
                                    <ErrorOutlineIcon fontSize="medium" />
                                </ListItemIcon>
                            </StyledListItem>
                            );
                        })}
                    </StyledList>
                </>
            )}
        </SectionCard>
    );
};

export default TalentProfileCompletionRate;
